
import { Shield, Map, Route, CheckCircle2, FileCheck, ArrowRight } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const CybersecurityRoadmap = () => {
  return (
    <LandingPageTemplate
      title="Cybersecurity Roadmap"
      description="Create a strategic cybersecurity roadmap that evolves with your business needs and aligns with industry best practices."
      keywords={[
        "cybersecurity roadmap", 
        "security strategy", 
        "cybersecurity planning", 
        "security maturity", 
        "IT security roadmap", 
        "security framework implementation"
      ]}
      canonicalUrl="/cybersecurity/cybersecurity-roadmap/"
      schemaData={{
        name: "Cybersecurity Roadmap Service",
        description: "Create a strategic cybersecurity roadmap that evolves with your business needs and aligns with industry best practices.",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Strategic"
        highlighted="Cybersecurity Roadmap"
        description="Build a clear path to mature security operations with a custom cybersecurity roadmap that aligns with your business goals and industry requirements."
        ctaText="Schedule a Roadmap Consultation"
        secondaryButtonText="Learn Our Approach"
        secondaryButtonLink="#process"
        modalTitle="Schedule Roadmap Consultation"
        modalDescription="Tell us about your security goals and challenges, and we'll help you build a strategic cybersecurity roadmap."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Cybersecurity Roadmap",
          service_interest: "Strategic Planning"
        }}
        heroPattern="roadmap"
      />

      <FeatureSection 
        title="A Strategic Approach to Security Maturity"
        subtitle="CYBERSECURITY ROADMAP"
        description="Our roadmap service provides a clear path forward with prioritized initiatives that align with your business objectives and security requirements."
        background="light"
        features={[
          {
            title: "Gap Analysis",
            description: "Comprehensive assessment against your chosen framework to identify security gaps and prioritize remediation efforts.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Strategic Planning",
            description: "Development of a multi-year cybersecurity strategy aligned with business goals and regulatory requirements.",
            icon: <Map className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Implementation Roadmap",
            description: "Detailed project plan with timelines, resources, and budget considerations for security initiatives.",
            icon: <Route className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Executive Reporting",
            description: "Clear, business-focused reporting to communicate security posture and progress to leadership teams.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection 
        title="Our Roadmap Development Process"
        subtitle="METHODOLOGY"
        description="A systematic approach to developing your cybersecurity strategy and implementation plan."
        id="process"
        steps={[
          {
            title: "Assessment",
            description: "Evaluate current security posture against industry frameworks and business requirements.",
            icon: <CheckCircle2 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Strategic Planning",
            description: "Define security objectives, priorities, and success metrics aligned with business goals.",
            icon: <Map className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Roadmap Development",
            description: "Create a detailed implementation plan with timelines, resources, and budget considerations.",
            icon: <Route className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Validation & Approval",
            description: "Review with stakeholders to ensure alignment with business priorities and obtain buy-in.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Implementation Support",
            description: "Provide guidance and expertise during execution of roadmap initiatives.",
            icon: <ArrowRight className="h-6 w-6 text-purple-600" />
          }
        ]}
        layout="vertical"
      />

      <BenefitSection 
        title="Benefits of a Strategic Security Roadmap"
        subtitle="BUSINESS VALUE"
        background="gradient"
        benefits={[
          {
            title: "Aligned Security Investments",
            description: "Ensure security spending directly supports business objectives and addresses top risks."
          },
          {
            title: "Regulatory Compliance",
            description: "Proactively address compliance requirements with a structured implementation approach."
          },
          {
            title: "Resource Optimization",
            description: "Maximize the impact of limited security resources by focusing on highest-value initiatives."
          },
          {
            title: "Measurable Progress",
            description: "Track security maturity improvement with clear metrics and milestone achievements."
          },
          {
            title: "Executive Visibility",
            description: "Provide clear reporting on security posture and progress to leadership and boards."
          },
          {
            title: "Risk Reduction",
            description: "Systematically address security gaps to reduce organizational cyber risk exposure."
          }
        ]}
      />

      <CtaSection 
        title="Ready to Build Your Cybersecurity Roadmap?"
        description="Get started with a strategic consultation to assess your current security posture and define a clear path forward."
        ctaText="Schedule Your Consultation"
        modalTitle="Cybersecurity Roadmap Consultation"
        modalDescription="Let's discuss your security goals and challenges to develop a customized roadmap for your organization."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Cybersecurity Roadmap",
          service_interest: "Strategic Planning"
        }}
      />
    </LandingPageTemplate>
  );
};

export default CybersecurityRoadmap;
